import { CareerDetailsInfo } from 'interfaces/CareerInterface'

// Collection of career-related conversation starters and interesting facts
const careerPrompts = [
  'Did you know that {careerName} professionals often have unique daily routines? What would you like to know about a typical day in this career?',
  "I see you're exploring {careerName}! This field has some fascinating aspects. What specific questions do you have about this career path?",
  'Welcome to your {careerName} exploration! This career has evolved significantly over the years. What interests you most about this field?',
  '{careerName} is known for {description}. What draws you to this particular career, and how can I help you learn more?',
  "Exploring {careerName}? That's exciting! This field offers unique opportunities and challenges. What would you like to discover first?",
  "Hey there! I see you're diving into {careerName}. This career path has some really interesting aspects. What questions can I help answer?",
  'Welcome to your {careerName} journey! This field is full of possibilities. What specific information are you looking for?',
  "I notice you're exploring {careerName} - great choice! This career has some unique characteristics. How can I help you understand this field better?",
]

const followUpPrompts = [
  'What aspects of this career interest you the most?',
  'Would you like to know about the day-to-day responsibilities?',
  'Are you curious about the education requirements?',
  'What would you like to know about career prospects and growth?',
  'Would you like to explore the skills needed for success?',
  'Are you interested in learning about salary expectations?',
]

// Function to get a random element from an array
const getRandomElement = <T>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)]
}

// Function to generate career-specific welcome message
export const generateCareerWelcomeMessage = (
  careerData: CareerDetailsInfo
): string => {
  const careerName = careerData.name
  const description = careerData.description || 'making a unique impact'

  // Choose a random prompt template
  const promptTemplate = getRandomElement(careerPrompts)

  // Replace placeholders in the template
  let welcomeMessage = promptTemplate
    .replace(/{careerName}/g, careerName)
    .replace(/{description}/g, description)

  // Add a follow-up question
  const followUpPrompt = getRandomElement(followUpPrompts)
  welcomeMessage += `\n\n${followUpPrompt}`

  return welcomeMessage
}

// Function to generate topic name for career sessions
export const generateCareerSessionTopic = (
  careerData: CareerDetailsInfo
): string => {
  return `${careerData.name} Exploration`
}

/**
 * Personalizes welcome message by replacing {{USER_NAME}} placeholder
 * @param message - The welcome message from backend
 * @param userFirstName - User's first name
 * @returns Personalized message with name replacement
 */
export const personalizeWelcomeMessage = (
  message: string,
  userFirstName?: string
): string => {
  if (!message) return message

  // Replace {{USER_NAME}} with user's first name or fallback
  const replacementName = userFirstName || 'there'
  return message.replace(/\{\{USER_NAME\}\}/g, replacementName)
}

/**
 * Checks if a welcome message needs personalization
 * @param welcomeMessage - The welcome message object from backend
 * @returns True if message needs name replacement, false if already personalized
 */
export const needsPersonalization = (welcomeMessage: {
  context?: {
    personalized: boolean
  } | null
}): boolean => {
  // If context is not provided or personalized is false, needs replacement
  return !welcomeMessage.context?.personalized
}

/**
 * Generates a fallback welcome message when API fails
 * @param userFirstName - User's first name
 * @returns Fallback welcome message
 */
export const generateFallbackWelcomeMessage = (
  userFirstName?: string
): string => {
  const name = userFirstName || 'there'
  return `Welcome to CareerSight, ${name}! I'm Career Sherpa, your AI assistant for career exploration and guidance. How can I help you today?`
}

/**
 * Generates fallback quick replies for welcome message
 * @returns Array of fallback quick replies
 */
export const generateFallbackQuickReplies = () => {
  return [
    {
      type: 'exploration' as const,
      text: 'Explore careers',
      action: 'message' as const,
      priority: 1,
    },
    {
      type: 'onboarding' as const,
      text: 'How does this work?',
      action: 'message' as const,
      priority: 2,
    },
    {
      type: 'career_action' as const,
      text: 'Get career advice',
      action: 'message' as const,
      priority: 3,
    },
  ]
}
